import { usePage } from '@inertiajs/react';
import axios from 'axios';
import LogOut from 'lucide-react/dist/esm/icons/log-out';
import UserRoundX from 'lucide-react/dist/esm/icons/user-round-x';
import { useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import type { PageProps } from '@/types';

export function ImpersonationTopBanner() {
  const { impersonation } = usePage<PageProps>().props;
  const [isExiting, setIsExiting] = useState(false);
  const isExitingRef = useRef(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const isImpersonating = impersonation?.is_impersonating ?? false;

  useLayoutEffect(() => {
    if (!isImpersonating) {
      document.documentElement.style.setProperty('--impersonation-banner-height', '0px');
      return;
    }

    const el = bannerRef.current;
    if (!el) {
      return;
    }

    // The rendered height varies with text wrapping and the safe-area inset,
    // so layouts must follow the measured banner, not a constant.
    const applyHeight = () => {
      document.documentElement.style.setProperty(
        '--impersonation-banner-height',
        `${el.getBoundingClientRect().height}px`
      );
    };

    applyHeight();

    const observer = new ResizeObserver(applyHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--impersonation-banner-height', '0px');
    };
  }, [isImpersonating]);

  if (!impersonation?.is_impersonating) {
    return null;
  }

  const handleExitImpersonation = async () => {
    if (isExitingRef.current) {
      return;
    }

    isExitingRef.current = true;
    setIsExiting(true);

    try {
      // A failed exit still answers with the dashboard to leave for, so both outcomes redirect.
      const response = await axios.post<{ success: boolean; redirect: string }>(
        impersonation.exit_url
      );

      window.location.href = response.data.redirect;
    } catch {
      isExitingRef.current = false;
      setIsExiting(false);
      toast.error('Could not exit impersonation.', { description: 'Please try again.' });
    }
  };

  return (
    <div
      ref={bannerRef}
      className={cn(
        'fixed top-0 right-0 left-0 z-50 flex min-h-10 items-center justify-between gap-2 px-2 pt-[env(safe-area-inset-top)] sm:px-4',
        'border-b border-amber-200 bg-amber-100 text-amber-900',
        'dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100'
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
        <UserRoundX className="size-4 shrink-0 text-amber-700 dark:text-amber-400" />
        <span className="truncate text-xs font-medium sm:text-sm">
          <span className="font-semibold">{impersonation.impersonated_user_name}</span>
          <span className="hidden text-amber-700 md:inline dark:text-amber-400">
            {' '}
            ({impersonation.impersonated_resource_name})
          </span>
        </span>
      </div>

      <button
        type="button"
        onClick={handleExitImpersonation}
        disabled={isExiting}
        className={cn(
          'flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium sm:gap-1.5 sm:px-3',
          'bg-amber-200 text-amber-900 hover:bg-amber-300 active:bg-amber-400',
          'dark:bg-amber-800 dark:text-amber-100 dark:hover:bg-amber-700 dark:active:bg-amber-600',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'transition-colors'
        )}
      >
        <LogOut className="size-3" />
        <span className="hidden sm:inline">{isExiting ? 'Exiting...' : 'Exit Impersonation'}</span>
        <span className="sm:hidden">{isExiting ? '...' : 'Exit'}</span>
      </button>
    </div>
  );
}
